// src/components/layout/Header.tsx
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { Moon, Sun, LogOut, User } from "lucide-react";
import { Button } from "../ui/Button";
import { ROUTES } from "../../utils/constants";
import { isLmsStudentSession } from "../../utils/lmsSession";

// ✅ COMMENTED: Language switch disabled (English only for now)
// import { LanguageSwitch } from "../ui/LanguageSwitch";

// ✅ COMMENTED: i18n disabled (English only for now)
// import { useTranslation } from "react-i18next";

interface HeaderProps {
  onMenuClick?: () => void;
}

const TEXT = {
  toggleSidebar: "Toggle sidebar",
  toggleTheme: "Toggle theme",
  profile: "Profile",
  signOut: "Sign out",
  brandName: "Tamil Katral Maiyam",
};

const ROLE_LABELS: Record<string, string> = {
  DEV_TEAM: "Dev Team",
  SCHOOL_ADMIN: "School Admin",
  TEACHER: "Teacher",
  STUDENT: "Student",
  PARENT: "Parent",
};

function getRoleLabel(role: string | undefined): string {
  if (!role) return "User";
  return ROLE_LABELS[role] ?? role.replace(/_/g, " ");
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout, isLmsLaunchSession } = useAuth();
  const { theme, toggleTheme } = useTheme();
  // ✅ COMMENTED: i18n disabled (English only for now)
  // const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN);
  };

  const getDashboardRoute = () => {
    if (!user) return ROUTES.LOGIN;
    if (isLmsStudentSession(user, isLmsLaunchSession)) {
      return ROUTES.STUDENT_TAMIL_TUTOR;
    }
    if (user.roles.includes("DEV_TEAM")) return ROUTES.DEV_DASHBOARD;
    if (user.roles.includes("SCHOOL_ADMIN")) return ROUTES.ADMIN_DASHBOARD;
    if (user.roles.includes("TEACHER")) return ROUTES.TEACHER_DASHBOARD;
    if (user.roles.includes("STUDENT")) return ROUTES.STUDENT_DASHBOARD;
    if (user.roles.includes("PARENT")) return ROUTES.PARENT_DASHBOARD;
    return ROUTES.LOGIN;
  };

  return (
    <header className="sticky top-0 z-50 h-16 shrink-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 shadow-lg border-b-4 border-white/20 backdrop-blur-xl">
      <div className="px-2 sm:px-6 lg:px-8 h-full">
        <div className="flex flex-nowrap items-center justify-between h-full min-w-0">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 overflow-hidden flex-1">
            {onMenuClick && (
              <button
                onClick={onMenuClick}
                aria-label={TEXT.toggleSidebar}
                className="flex flex-col items-center justify-center gap-1.5 rounded-xl sm:rounded-2xl p-2 sm:p-3 transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/50 shrink-0"
              >
                <span className="h-1 w-7 rounded-full bg-white/90" />
                <span className="h-1 w-7 rounded-full bg-white/90" />
                <span className="h-1 w-7 rounded-full bg-white/90" />
              </button>
            )}

            <Link
              to={getDashboardRoute()}
              className="hidden sm:flex items-center min-w-0 px-2"
              title={TEXT.brandName}
            >
              <span className="text-white font-extrabold text-xl tracking-tight truncate">
                {TEXT.brandName}
              </span>
            </Link>
          </div>

          <div className="flex flex-nowrap items-center gap-2 sm:gap-4 shrink-0">
            {/* ✅ COMMENTED: Language switch disabled (English only for now) */}
            {/*
            <LanguageSwitch />
            */}

            <button
              onClick={toggleTheme}
              className="p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-white/10 hover:bg-white/25 backdrop-blur-sm shadow-lg transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/50"
              aria-label={TEXT.toggleTheme}
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-300 drop-shadow-lg" />
              ) : (
                <Moon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-200 drop-shadow-lg" />
              )}
            </button>

            {user && (
              <>
                <div className="flex items-center gap-2 min-w-0">
                  <Link
                    to="/profile"
                    className="relative p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-white/10 hover:bg-white/20 backdrop-blur-sm shadow-lg transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/50 shrink-0"
                    aria-label={TEXT.profile}
                  >
                    <div className="absolute -top-0.5 -right-0.5 w-3 h-3 sm:w-4 sm:h-4 bg-green-400 border-2 border-white rounded-full shadow-lg" />
                    <User className="w-5 h-5 sm:w-6 sm:h-6 text-white drop-shadow-lg" />
                  </Link>
                  <span className="hidden sm:inline text-sm font-semibold text-white drop-shadow-md bg-white/20 px-2 py-1 rounded-lg truncate max-w-[8rem]">
                    {getRoleLabel(user.roles[0])}
                  </span>
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleLogout}
                  className="bg-white/10 hover:bg-white/10 backdrop-blur-sm shadow-lg border-white/30 font-bold tracking-wide px-3 sm:px-6 py-2 sm:py-2.5 rounded-xl sm:rounded-2xl transition-colors focus-visible:ring-4 focus-visible:ring-white/50 min-w-[2.75rem] sm:min-w-0"
                  aria-label={TEXT.signOut}
                >
                  <span className="flex items-center justify-center gap-2">
                    <LogOut className="w-5 h-5 shrink-0" />
                    <span className="hidden sm:inline">{TEXT.signOut}</span>
                  </span>
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
